(() => {
  if (customElements.get('predictive-search')) return;

  const DEBOUNCE_MS = 300;
  const MIN_QUERY_LENGTH = 3;
  const MAX_RESULTS = 6;
  const CLOSE_SUPPRESS_MS = 500;
  const DESKTOP_MEDIA = '(min-width: 750px)';

  const instances = new Set();

  const onDocumentPointerDown = (e) => {
    instances.forEach((instance) => {
      instance.handleOutsidePointer(e);
    });
  };

  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.onInput = this.onInput.bind(this);
      this.onFocus = this.onFocus.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onSearchBarPointerDown = this.onSearchBarPointerDown.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);
      this.debounceTimer = null;
      this.showTimer = null;
      this.activeIndex = -1;
      this.lastQuery = '';
      this.suppressCloseUntil = 0;
      this.interactionGen = 0;
      this.collectionProducts = [];
    }

    connectedCallback() {
      this.input = this.querySelector('.js-predictive-search-input');
      this.submitBtn = this.querySelector('.js-predictive-search-submit');
      this.searchBar = this.querySelector('.header__search-bar');
      this.resultsContainer = this.querySelector('.js-predictive-search-results');
      if (!this.input || !this.resultsContainer) return;

      this.collectionProducts = this.getCollectionProductsJson();

      this.searchBar?.addEventListener('pointerdown', this.onSearchBarPointerDown, true);
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('focus', this.onFocus);
      this.input.addEventListener('click', this.onClick);
      this.input.addEventListener('keydown', this.onKeyDown);
      this.submitBtn?.addEventListener('click', this.onSubmit);

      instances.add(this);
      if (instances.size === 1) {
        document.addEventListener('pointerdown', onDocumentPointerDown, false);
      }
    }

    disconnectedCallback() {
      this.searchBar?.removeEventListener('pointerdown', this.onSearchBarPointerDown, true);
      this.input?.removeEventListener('input', this.onInput);
      this.input?.removeEventListener('focus', this.onFocus);
      this.input?.removeEventListener('click', this.onClick);
      this.input?.removeEventListener('keydown', this.onKeyDown);
      this.submitBtn?.removeEventListener('click', this.onSubmit);
      instances.delete(this);
      if (instances.size === 0) {
        document.removeEventListener('pointerdown', onDocumentPointerDown, false);
      }
      this.unbindViewportListeners();
      clearTimeout(this.debounceTimer);
      clearTimeout(this.showTimer);
    }

    getHeaderNavigation() {
      return this.closest('header-navigation');
    }

    getSearchStrings() {
      const header = this.getHeaderNavigation();
      return {
        noResultsBefore: header?.dataset?.searchNoResultsBefore || 'No results found for',
        noResultsAfter:
          header?.dataset?.searchNoResultsAfter ||
          '. Check the spelling or use a different word or phrase.',
        productsTitle: header?.dataset?.productsTitle || 'Products',
      };
    }

    getCollectionProductsJson() {
      const script = this.getHeaderNavigation()?.querySelector('[data-collection-products-json]');
      if (!script) return [];
      try {
        const data = JSON.parse(script.textContent || '[]');
        return Array.isArray(data) ? data : [];
      } catch {
        return [];
      }
    }

    hasCollectionProducts() {
      return this.collectionProducts.length > 0;
    }

    getTrendingHtml() {
      const script = this.getHeaderNavigation()?.querySelector('script[data-predictive-search-trending-html]');
      return script?.textContent?.trim() || '';
    }

    usesFixedDropdown() {
      return !this.classList.contains('header__search--mobile') && window.matchMedia(DESKTOP_MEDIA).matches;
    }

    hasTrending() {
      const html = this.getTrendingHtml();
      return html.length > 0 && html.includes('header__search-result');
    }

    bumpInteraction() {
      this.interactionGen += 1;
      clearTimeout(this.debounceTimer);
      clearTimeout(this.showTimer);
      return this.interactionGen;
    }

    suppressCloseBriefly() {
      this.suppressCloseUntil = Date.now() + CLOSE_SUPPRESS_MS;
    }

    onSearchBarPointerDown() {
      this.suppressCloseBriefly();
    }

    handleOutsidePointer(e) {
      if (Date.now() < this.suppressCloseUntil) return;
      if (!this.isOpen()) return;
      if (this.contains(e.target)) return;
      this.close();
    }

    queueInteraction() {
      const gen = this.bumpInteraction();
      this.suppressCloseBriefly();
      clearTimeout(this.showTimer);
      this.showTimer = setTimeout(() => {
        if (gen !== this.interactionGen) return;
        if (document.activeElement !== this.input) return;
        this.runInteraction();
      }, 0);
    }

    onSubmit(e) {
      e.preventDefault();
      this.suppressCloseBriefly();
      const gen = this.bumpInteraction();
      const query = this.input.value.trim();
      if (query.length < MIN_QUERY_LENGTH) {
        this.showTrendingOrClose();
        return;
      }
      this.runSearch(query, gen);
    }

    onInput() {
      const query = this.input.value.trim();
      this.input.classList.toggle('header__search-input--has-value', query.length > 0);
      const gen = this.bumpInteraction();
      this.suppressCloseBriefly();

      if (query.length < MIN_QUERY_LENGTH) {
        this.showTimer = setTimeout(() => {
          if (gen !== this.interactionGen) return;
          this.showTrendingOrClose();
        }, 0);
        return;
      }

      this.debounceTimer = setTimeout(() => {
        if (gen !== this.interactionGen) return;
        const currentQuery = this.input.value.trim();
        if (currentQuery.length < MIN_QUERY_LENGTH) {
          this.showTrendingOrClose();
          return;
        }
        this.runSearch(currentQuery, gen);
      }, DEBOUNCE_MS);
    }

    onFocus() {
      const headerNav = this.getHeaderNavigation();
      if (headerNav?.isMenuOpen?.() && this.classList.contains('header__search--mobile')) {
        headerNav.stabilizePageScroll();
      }
      this.queueInteraction();
    }

    onClick() {
      this.queueInteraction();
    }

    runInteraction() {
      const query = this.input.value.trim();
      this.input.classList.toggle('header__search-input--has-value', query.length > 0);

      if (query.length < MIN_QUERY_LENGTH) {
        this.showTrendingOrClose();
        return;
      }

      const gen = this.interactionGen;
      this.runSearch(query, gen);
    }

    showTrendingOrClose() {
      if (this.hasTrending()) {
        this.showTrending();
      } else {
        this.close();
      }
    }

    showTrending() {
      const html = this.getTrendingHtml();
      if (!html || !html.includes('header__search-result')) {
        this.close();
        return;
      }

      this.bumpInteraction();
      this.activeIndex = -1;
      this.lastQuery = '';
      this.mountHtml(html);
    }

    mountHtml(html) {
      this.resultsContainer.innerHTML = html;
      this.open();
    }

    onKeyDown(e) {
      if (!this.isOpen()) return;

      const items = this.getNavigableItems();

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          this.activeIndex = Math.min(this.activeIndex + 1, items.length - 1);
          this.updateActiveDescendant(items);
          break;

        case 'ArrowUp':
          e.preventDefault();
          this.activeIndex = Math.max(this.activeIndex - 1, -1);
          this.updateActiveDescendant(items);
          if (this.activeIndex === -1) this.input.focus();
          break;

        case 'Enter':
          e.preventDefault();
          if (this.activeIndex >= 0 && items[this.activeIndex]) {
            items[this.activeIndex].click();
          } else {
            const query = this.input.value.trim();
            if (query.length >= MIN_QUERY_LENGTH) {
              const gen = this.interactionGen;
              this.runSearch(query, gen);
            }
          }
          break;

        case 'Escape':
          e.preventDefault();
          this.close();
          this.input.focus();
          break;
      }
    }

    getNavigableItems() {
      return [...this.resultsContainer.querySelectorAll('.header__search-result')];
    }

    updateActiveDescendant(items) {
      items.forEach((item, i) => {
        item.setAttribute('aria-selected', String(i === this.activeIndex));
      });

      if (this.activeIndex >= 0 && items[this.activeIndex]) {
        this.input.setAttribute('aria-activedescendant', items[this.activeIndex].id || '');
        items[this.activeIndex].scrollIntoView({ block: 'nearest' });
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    runSearch(query, gen) {
      if (gen !== this.interactionGen) return;
      this.searchCollection(query);
    }

    searchCollection(query) {
      this.lastQuery = query;

      if (!this.hasCollectionProducts()) {
        this.renderEmpty(query);
        return;
      }

      const lower = query.toLowerCase();
      const matches = this.collectionProducts
        .filter((product) => (product.title || '').toLowerCase().includes(lower))
        .sort((a, b) => this.compareProductAvailability(a, b))
        .slice(0, MAX_RESULTS);

      this.renderResults(matches, query);
    }

    compareProductAvailability(a, b) {
      const aAvailable = a?.available !== false;
      const bAvailable = b?.available !== false;
      if (aAvailable === bAvailable) return 0;
      return aAvailable ? -1 : 1;
    }

    buildPanelShell(title) {
      const inner = document.createElement('div');
      inner.className = 'header__search-dropdown-inner';

      const header = document.createElement('div');
      header.className = 'header__search-dropdown-header';

      const titleEl = document.createElement('p');
      titleEl.className = 'header__search-dropdown-title';
      titleEl.textContent = title;
      header.appendChild(titleEl);

      const divider = document.createElement('div');
      divider.className = 'header__search-dropdown-divider';
      divider.setAttribute('aria-hidden', 'true');

      const results = document.createElement('div');
      results.className = 'header__search-dropdown-results';

      inner.append(header, divider, results);
      return { inner, results };
    }

    renderResults(products, query) {
      this.activeIndex = -1;

      if (!products.length) {
        this.renderEmpty(query);
        return;
      }

      const { productsTitle } = this.getSearchStrings();
      const { inner, results } = this.buildPanelShell(productsTitle);
      const fragment = document.createDocumentFragment();

      products.forEach((product, index) => {
        fragment.appendChild(this.buildProductItem(product, query, index));
      });

      results.appendChild(fragment);
      this.resultsContainer.replaceChildren(inner);
      this.open();
    }

    renderEmpty(query) {
      this.activeIndex = -1;

      const { noResultsBefore, noResultsAfter, productsTitle } = this.getSearchStrings();
      const { inner, results } = this.buildPanelShell(productsTitle);

      const empty = document.createElement('p');
      empty.className = 'header__search-empty';
      empty.setAttribute('role', 'status');
      empty.innerHTML =
        `${this.escHtml(noResultsBefore)}&nbsp;“<span class="header__search-empty-query">${this.escHtml(query)}</span>”${this.escHtml(noResultsAfter)}`;

      results.appendChild(empty);
      this.resultsContainer.replaceChildren(inner);
      this.open();
    }

    buildProductItem(product, query, index) {
      const item = document.createElement('a');
      item.href = product.url;
      item.className = 'header__search-result';
      item.setAttribute('aria-selected', 'false');
      item.id = `predictive-result-${this.resultsContainer.id}-${index}`;

      const imgSrc = product.image ? this.getSizedImageUrl(product.image, '160x') : '';
      const isSale = Boolean(product.on_sale);
      const price = this.escHtml(product.price_formatted || '');
      const comparePrice = isSale ? this.escHtml(product.compare_at_price_formatted || '') : '';

      item.innerHTML =
        (imgSrc
          ? `<img class="header__search-result-img" src="${this.escAttr(imgSrc)}" width="80" height="80" loading="lazy" alt="">`
          : '<div class="header__search-result-img header__search-result-img--placeholder" aria-hidden="true"></div>') +
        '<span class="header__search-result-info">' +
          `<span class="header__search-result-title">${this.highlightQuery(product.title, query)}</span>` +
          '<span class="header__search-result-price">' +
            `<span class="header__search-result-price-current${isSale ? ' header__search-result-price-current--sale' : ''}">${price}</span>` +
            (isSale ? `<span class="header__search-result-price-compare">${comparePrice}</span>` : '') +
          '</span>' +
        '</span>';

      return item;
    }

    positionDropdown() {
      if (!this.usesFixedDropdown() || !this.searchBar) {
        this.clearDropdownPosition();
        return;
      }

      const bar = this.searchBar.getBoundingClientRect();
      this.resultsContainer.classList.add('header__search-dropdown--fixed');
      this.resultsContainer.style.position = 'fixed';
      this.resultsContainer.style.top = `${Math.round(bar.bottom + 12)}px`;
      this.resultsContainer.style.left = `${Math.round(bar.left)}px`;
      this.resultsContainer.style.width = `${Math.round(bar.width)}px`;
      this.resultsContainer.style.right = 'auto';
      this.resultsContainer.style.zIndex = '10000';
    }

    clearDropdownPosition() {
      this.resultsContainer.classList.remove('header__search-dropdown--fixed');
      this.resultsContainer.style.position = '';
      this.resultsContainer.style.top = '';
      this.resultsContainer.style.left = '';
      this.resultsContainer.style.width = '';
      this.resultsContainer.style.right = '';
      this.resultsContainer.style.zIndex = '';
    }

    bindViewportListeners() {
      if (this._viewportListenersBound) return;
      this._viewportListenersBound = true;
      window.addEventListener('resize', this.onViewportChange, { passive: true });
      window.addEventListener('scroll', this.onViewportChange, { passive: true });
    }

    unbindViewportListeners() {
      if (!this._viewportListenersBound) return;
      this._viewportListenersBound = false;
      window.removeEventListener('resize', this.onViewportChange);
      window.removeEventListener('scroll', this.onViewportChange);
    }

    onViewportChange() {
      if (!this.isOpen()) return;
      this.positionDropdown();
    }

    open() {
      this.suppressCloseBriefly();
      this.resultsContainer.removeAttribute('hidden');
      this.resultsContainer.hidden = false;
      this.resultsContainer.classList.add('header__search-dropdown--open');
      this.input.setAttribute('aria-expanded', 'true');

      if (this.usesFixedDropdown()) {
        this.bindViewportListeners();
        requestAnimationFrame(() => {
          if (!this.isOpen()) return;
          this.positionDropdown();
        });
      } else {
        this.clearDropdownPosition();
      }
    }

    close() {
      this.unbindViewportListeners();
      this.clearDropdownPosition();
      this.resultsContainer.hidden = true;
      this.resultsContainer.setAttribute('hidden', '');
      this.resultsContainer.classList.remove('header__search-dropdown--open');
      this.input.setAttribute('aria-expanded', 'false');
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
    }

    isOpen() {
      return this.resultsContainer.classList.contains('header__search-dropdown--open');
    }

    highlightQuery(text, query) {
      if (!query) return this.escHtml(text);
      const idx = text.toLowerCase().indexOf(query.toLowerCase());
      if (idx === -1) return this.escHtml(text);
      return (
        this.escHtml(text.slice(0, idx)) +
        `<mark class="header__search-highlight">${this.escHtml(text.slice(idx, idx + query.length))}</mark>` +
        this.escHtml(text.slice(idx + query.length))
      );
    }

    getSizedImageUrl(src, size) {
      if (!src) return '';
      if (src.includes('_' + size)) return src;
      const match = src.match(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i);
      if (!match) return src;
      return src.replace(match[0], `_${size}${match[0]}`);
    }

    escHtml(str) {
      return String(str).replace(/[&<>"']/g, (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
      );
    }

    escAttr(str) {
      return String(str).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();
